import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Question, X, PlayCircle, ArrowRight, FilmSlate } from '@phosphor-icons/react';
import api from '../../lib/api';

/**
 * Maps current pathname to the `module` tag stored on training videos.
 * Keep in sync with the dropdown in SuperAdminTraining.jsx.
 */
function moduleFromPath(pathname) {
  if (pathname.startsWith('/dashboard'))       return 'dashboard';
  if (pathname.startsWith('/master-styles'))   return 'master_styles';
  if (pathname.startsWith('/purchase-orders')) return 'purchase_orders';
  if (pathname.startsWith('/po-completion'))   return 'purchase_orders';
  if (pathname.startsWith('/cutting'))         return 'cutting';
  if (pathname.startsWith('/sampling'))        return 'sampling';
  if (pathname.startsWith('/lots'))            return 'lots';
  if (pathname.startsWith('/production'))      return 'production';
  if (pathname.startsWith('/contractors'))     return 'contractors';
  if (pathname.startsWith('/rate-master'))     return 'contractors';
  if (pathname.startsWith('/invoices'))        return 'finance';
  if (pathname.startsWith('/ledgers'))         return 'finance';
  if (pathname.startsWith('/settings'))        return 'settings';
  return '';
}

/**
 * Floating ? help button. Shows a slide-in panel with training videos
 * tagged for the current module + a "Browse all" link.
 */
export default function HelpButton() {
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const mod = moduleFromPath(location.pathname);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/training/videos' + (mod ? `?module=${mod}` : ''));
      setVideos(r.data || []);
    } catch { setVideos([]); }
  }, [mod]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Lock body scroll while panel is open (mobile-first)
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <button
        data-testid="topbar-help-btn"
        onClick={() => setOpen(true)}
        aria-label="Training & help"
        className="h-9 w-9 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center text-stone-500 dark:text-stone-400"
        title="Help & training videos"
      >
        <Question size={16} weight="bold" />
      </button>

      {/* Slide-in panel */}
      {open && (
        <>
          <div
            data-testid="help-panel-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm"
          />
          <aside
            data-testid="help-panel"
            className="fixed top-0 right-0 bottom-0 z-[56] w-full sm:w-[420px] bg-white dark:bg-stone-900 shadow-2xl flex flex-col animate-slide-in"
          >
            <div className="px-5 py-4 border-b border-stone-100 dark:border-stone-800 flex items-center gap-3 bg-white dark:bg-stone-900">
              <div className="h-10 w-10 rounded-xl bg-amber-400 text-stone-900 flex items-center justify-center shrink-0">
                <Question size={18} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500 font-bold">Need a hand?</p>
                <h3 className="text-sm font-bold text-stone-800 dark:text-stone-100">
                  {mod ? `Tutorials for this page` : 'Training & onboarding'}
                </h3>
              </div>
              <button onClick={() => setOpen(false)} data-testid="help-panel-close"
                className="h-9 w-9 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white dark:bg-stone-900">
              {videos.length === 0 ? (
                <div className="text-center py-12">
                  <FilmSlate size={32} className="mx-auto text-stone-300 mb-2" />
                  <p className="text-sm text-stone-500">
                    {mod ? `No tutorials for this page yet.` : 'No tutorials available yet.'}
                  </p>
                  <p className="text-[11px] text-stone-400 mt-1">Check back soon.</p>
                </div>
              ) : videos.map(v => (
                <button key={v.id} onClick={() => setActive(v)}
                  data-testid={`help-video-${v.id}`}
                  className="w-full flex gap-3 text-left bg-stone-50 dark:bg-stone-800/50 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-xl p-2 transition group">
                  <div className="relative w-28 aspect-video rounded-lg overflow-hidden bg-stone-200 dark:bg-stone-800 shrink-0">
                    <img src={v.thumbnail_url} alt={v.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <PlayCircle size={22} weight="fill" className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B4D5F]">{v.category}</p>
                    <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 leading-snug line-clamp-2">{v.title}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-stone-100 dark:border-stone-800 p-4 bg-white dark:bg-stone-900">
              <button
                data-testid="help-browse-all"
                onClick={() => { setOpen(false); navigate('/training'); }}
                className="w-full h-11 rounded-xl bg-[#6B4D5F] hover:bg-[#5A3D4E] text-white text-sm font-bold flex items-center justify-center gap-2"
              >
                Browse the full Academy <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </aside>
        </>
      )}

      {active && <PlayerOverlay video={active} onClose={() => setActive(null)} />}

      <style>{`
        @keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slide-in 0.25s ease-out; }
      `}</style>
    </>
  );
}

function PlayerOverlay({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose} data-testid="help-player-overlay">
      <div className="w-full max-w-4xl bg-stone-900 rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="text-sm font-bold text-white truncate">{video.title}</h3>
          <button onClick={onClose} className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X size={16} />
          </button>
        </div>
        <div className="aspect-video bg-black">
          <iframe
            src={`${video.embed_url}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
}
